// PROPOSITO: vaciar la cola sin conexion. Es lo que hace que el modo avion no pierda nada.
// DEPENDENCIAS: store de sincronizacion y los servicios que escriben de verdad.
// NO OBVIO (1): navigator.onLine MIENTE. Dice true con wifi de gimnasio sin salida, con portal
// cautivo y con datos agotados. Por eso el estado real se decide por si una escritura funciona,
// no por lo que diga el navegador.
// NO OBVIO (2): se reintenta con espera creciente. Reintentar cada segundo contra un servidor caido
// agota la bateria de la alumna y no acelera nada.
// NO OBVIO (3): se vacia tambien al volver a la app (visibilitychange) y al enfocar la ventana, no
// solo con el evento 'online'. Volver del bloqueo de pantalla no dispara 'online' en iOS.

import { usarCola } from '@/store/sincronizacion'
import type { AccionPendiente } from '@/store/sincronizacion'
import { completarSesion, registrarSerie } from '@/services/registros.service'
import { marcarComida } from '@/services/comidas.service'
import { registrarMedicion } from '@/services/mediciones.service'

const ESPERAS_MS = [0, 2_000, 5_000, 15_000, 30_000, 60_000]

async function ejecutar(accion: AccionPendiente): Promise<void> {
  const carga = accion.carga as never
  switch (accion.tipo) {
    case 'registrar_serie':
      return registrarSerie(carga)
    case 'completar_sesion': {
      const c = accion.carga as { sesionId: string; sensacion: number | null; nota: string | null }
      return completarSesion(c.sesionId, c.sensacion, c.nota)
    }
    case 'marcar_comida':
      return marcarComida(carga)
    case 'registrar_medicion':
      return registrarMedicion(carga)
  }
}

let corriendo = false
let reintento: ReturnType<typeof setTimeout> | null = null

function programarReintento(intentos: number): void {
  if (reintento !== null) clearTimeout(reintento)
  const espera = ESPERAS_MS[Math.min(intentos, ESPERAS_MS.length - 1)] ?? 60_000
  reintento = setTimeout(() => {
    void vaciarCola()
  }, espera)
}

/** Vacia la cola. Es seguro llamarla muchas veces: solo una pasada corre a la vez. */
export async function vaciarCola(): Promise<void> {
  if (corriendo) return
  const pendientes = usarCola.getState().cola
  if (pendientes.length === 0) {
    usarCola.getState().marcarEnLinea(true)
    return
  }

  corriendo = true
  usarCola.getState().marcarSincronizando(true)
  let fallo: AccionPendiente | null = null

  try {
    // En orden y de a una: una serie 3 no puede subir antes que la sesion exista.
    for (const accion of [...pendientes]) {
      try {
        await ejecutar(accion)
        usarCola.getState().confirmar(accion.clienteId)
        // Si una escritura paso, hay red de verdad — diga lo que diga navigator.onLine.
        usarCola.getState().marcarEnLinea(true)
      } catch {
        usarCola.getState().fallar(accion.clienteId)
        fallo = accion
        break
      }
    }
  } finally {
    corriendo = false
    usarCola.getState().marcarSincronizando(false)
  }

  if (fallo) {
    usarCola.getState().marcarEnLinea(false)
    programarReintento(fallo.intentos + 1)
  } else if (reintento !== null) {
    clearTimeout(reintento)
    reintento = null
  }
}

/** Se llama una vez desde main.tsx. Escucha todo lo que puede significar "volvio la red". */
export function escucharLaRed(): () => void {
  const intentar = () => {
    void vaciarCola()
  }
  const alCaer = () => {
    usarCola.getState().marcarEnLinea(false)
  }
  const alVolverALaApp = () => {
    if (document.visibilityState === 'visible') intentar()
  }

  window.addEventListener('online', intentar)
  window.addEventListener('offline', alCaer)
  window.addEventListener('focus', intentar)
  document.addEventListener('visibilitychange', alVolverALaApp)
  intentar()

  return () => {
    window.removeEventListener('online', intentar)
    window.removeEventListener('offline', alCaer)
    window.removeEventListener('focus', intentar)
    document.removeEventListener('visibilitychange', alVolverALaApp)
    if (reintento !== null) clearTimeout(reintento)
  }
}
